import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLUMNS = [
  { key: "pending", label: "Pending", next: "preparing", action: "Start prepping" },
  { key: "preparing", label: "Preparing", next: "ready", action: "Mark ready" },
  { key: "ready", label: "Ready", next: "served", action: "Mark served" },
  { key: "served", label: "Served (today)", next: null, action: null },
];

export default function KDS() {
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/active");
      // also pull recent served
      const served = (await api.get("/orders", { params: { status: "served", limit: 12 } })).data;
      setOrders([...data, ...served]);
    } catch (_e) {
      /* tolerate transient errors during polling */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000); // poll for real-time-ish updates
    return () => clearInterval(id);
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Updated → ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Kitchen display"
        title="KDS — Live orders"
        subtitle="Drag your focus across columns. Tap an order to advance it through the pipeline."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kds-board">
        {COLUMNS.map((col) => {
          const list = orders.filter((o) => o.status === col.key);
          return (
            <div key={col.key} className="bento p-3 min-h-[60vh]" data-testid={`kds-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div>
                  <div className="label-eyebrow">{col.label}</div>
                  <div className="font-display font-extrabold text-2xl">{list.length}</div>
                </div>
              </div>
              <div className="space-y-3 stagger">
                {list.map((o) => (
                  <div key={o.id} className={`ticket ticket-${col.key} p-3`} data-testid={`kds-ticket-${o.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-display font-bold text-base">{o.order_number.split("-").pop()}</div>
                        <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">
                          {o.type}{o.table_number ? ` · T${o.table_number}` : ""}
                        </div>
                      </div>
                      <div className="text-[11px] font-mono text-[var(--text-secondary)]">{fmtTime(o.created_at)}</div>
                    </div>
                    <div className="my-2 border-t border-dashed border-[#EAE7E0]"></div>
                    <ul className="space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="text-sm flex justify-between">
                          <span><span className="font-bold mr-2">{it.quantity}×</span>{it.name}</span>
                        </li>
                      ))}
                    </ul>
                    {col.next && (
                      <Button onClick={() => setStatus(o.id, col.next)} className="w-full mt-3 btn-primary h-9 text-xs font-bold" data-testid={`kds-advance-${o.id}`}>
                        {col.action} →
                      </Button>
                    )}
                  </div>
                ))}
                {list.length === 0 && (
                  <div className="text-xs text-[var(--text-secondary)] py-8 text-center">No orders here.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
